import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddSecurityAllowlistData,
  ApiClient,
  CreateSecurityBlockData,
  ExtendSecurityBlockData,
  ListSecurityActionsQuery,
  ListSecurityBlocksQuery,
  ListSecurityEventsQuery,
  ListSecurityNotificationsQuery,
  RemoveSecurityAllowlistData,
  RevokeSecurityBlockData,
  ScriptListQuery,
  SecurityBlockPagePreviewData,
  UpdateSecurityPolicyData,
} from '@luacentrix/api-client';
import type {
  BuildLoaderInput,
  CreateScriptInput,
  ListLicensesQuery,
  ListProjectsQuery,
  ObfuscateScriptInput,
  OpaqueCursor,
  SaveKeysystemInput,
  UpdateScriptPlacesInput,
} from '@luacentrix/contracts';

export function useHealth(api: ApiClient) {
  return useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => api.health(signal),
    retry: 1,
    refetchInterval: 60_000,
  });
}

export function usePlatformStatus(api: ApiClient) {
  return useQuery({
    queryKey: ['platform-status'],
    queryFn: ({ signal }) => api.status(signal),
    retry: 1,
    refetchInterval: 30_000,
  });
}

export function useProjects(api: ApiClient, query: ListProjectsQuery) {
  return useQuery({
    queryKey: ['projects', query],
    queryFn: ({ signal }) => api.projects.list(query, { signal }),
    retry: 0,
  });
}

export function useLicenses(api: ApiClient, query: ListLicensesQuery) {
  return useQuery({
    queryKey: ['licenses', query],
    queryFn: ({ signal }) => api.licenses.list(query, { signal }),
    retry: 0,
  });
}

export interface CursorPager {
  cursor: OpaqueCursor | undefined;
  pageNumber: number;
  canPrevious: boolean;
  openNext: (nextCursor: OpaqueCursor | null) => void;
  goPrevious: () => void;
  reset: () => void;
}

export function useCursorPager(): CursorPager {
  const [cursors, setCursors] = useState<Array<OpaqueCursor | undefined>>([undefined]);

  const openNext = useCallback((nextCursor: OpaqueCursor | null) => {
    if (nextCursor === null) return;
    setCursors(current =>
      current[current.length - 1] === nextCursor ? current : [...current, nextCursor],
    );
  }, []);

  const goPrevious = useCallback(() => {
    setCursors(current => (current.length > 1 ? current.slice(0, -1) : current));
  }, []);

  const reset = useCallback(() => {
    setCursors([undefined]);
  }, []);

  return {
    cursor: cursors[cursors.length - 1],
    pageNumber: cursors.length,
    canPrevious: cursors.length > 1,
    openNext,
    goPrevious,
    reset,
  };
}

export const SECURITY_QUERY_ROOT = ['security'] as const;

export function useAccountSession(api: ApiClient) {
  return useQuery({
    queryKey: ['account', 'session'],
    queryFn: ({ signal }) => api.account.session({ signal }),
    retry: 0,
    staleTime: 60_000,
  });
}

export function useSecurityStatus(api: ApiClient, reference: string) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'status', reference],
    queryFn: ({ signal }) => api.security.status({ ref: reference }, { signal }),
    enabled: reference.length > 0,
    retry: 0,
  });
}

export function useSecurityStats(api: ApiClient) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'stats'],
    queryFn: ({ signal }) => api.security.stats({ signal }),
    retry: 0,
  });
}

export function useSecurityEvents(api: ApiClient, query: ListSecurityEventsQuery) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'events', query],
    queryFn: ({ signal }) => api.security.events(query, { signal }),
    retry: 0,
  });
}

export function useSecurityBlocks(api: ApiClient, query: ListSecurityBlocksQuery) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'blocks', query],
    queryFn: ({ signal }) => api.security.blocks(query, { signal }),
    retry: 0,
  });
}

export function useSecurityActions(api: ApiClient, query: ListSecurityActionsQuery) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'actions', query],
    queryFn: ({ signal }) => api.security.actions(query, { signal }),
    retry: 0,
  });
}

export function useSecurityNotifications(api: ApiClient, query: ListSecurityNotificationsQuery) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'notifications', query],
    queryFn: ({ signal }) => api.security.notifications(query, { signal }),
    retry: 0,
  });
}

export function useUnreadNotificationCount(api: ApiClient, enabled: boolean) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'notifications', { limit: 1 }],
    queryFn: ({ signal }) => api.security.notifications({ limit: 1 }, { signal }),
    enabled,
    retry: 0,
    refetchInterval: enabled ? 60_000 : false,
  });
}

export function useAcknowledgeNotificationMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (input: { notificationId: string; action: 'acknowledge' | 'dismiss' }) =>
      input.action === 'dismiss'
        ? api.security.dismissNotification(input.notificationId)
        : api.security.acknowledgeNotification(input.notificationId),
    onSuccess: invalidate,
  });
}

export function useSecurityIpQuery(api: ApiClient, address: string) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'ip', address],
    queryFn: ({ signal }) => api.security.ip({ address }, { signal }),
    enabled: address.trim().length > 0,
    retry: 0,
  });
}

export function useSecurityAllowlist(api: ApiClient) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'allowlist'],
    queryFn: ({ signal }) => api.security.allowlist.list({ signal }),
    retry: 0,
  });
}

export function useSecurityPolicy(api: ApiClient) {
  return useQuery({
    queryKey: [...SECURITY_QUERY_ROOT, 'policy'],
    queryFn: ({ signal }) => api.security.policy({ signal }),
    retry: 0,
  });
}

export function useSecurityInvalidation() {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: SECURITY_QUERY_ROOT });
  }, [client]);
}

export function useBlockIpMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (data: CreateSecurityBlockData) => api.security.block(data),
    onSuccess: invalidate,
  });
}

export function useUnblockIpMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (input: { blockId: string; data: RevokeSecurityBlockData }) =>
      api.security.unblock(input.blockId, input.data),
    onSuccess: invalidate,
  });
}

export function useExtendBlockMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (input: { blockId: string; data: ExtendSecurityBlockData }) =>
      api.security.extend(input.blockId, input.data),
    onSuccess: invalidate,
  });
}

export function useAddAllowlistMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (data: AddSecurityAllowlistData) => api.security.allowlist.add(data),
    onSuccess: invalidate,
  });
}

export function useRemoveAllowlistMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (input: { allowlistId: string; data: RemoveSecurityAllowlistData }) =>
      api.security.allowlist.remove(input.allowlistId, input.data),
    onSuccess: invalidate,
  });
}

export function useUpdatePolicyMutation(api: ApiClient) {
  const invalidate = useSecurityInvalidation();
  return useMutation({
    mutationFn: (data: UpdateSecurityPolicyData) => api.security.updatePolicy(data),
    onSuccess: invalidate,
  });
}

export function useBlockPagePreviewMutation(api: ApiClient) {
  return useMutation({
    mutationFn: (data: SecurityBlockPagePreviewData) => api.security.preview(data),
  });
}

const KEYSYSTEM_QUERY_ROOT = ['keysystem'] as const;

export function useKeysystem(api: ApiClient, enabled: boolean) {
  return useQuery({
    queryKey: [...KEYSYSTEM_QUERY_ROOT],
    queryFn: ({ signal }) => api.keysystem.get({ signal }),
    retry: 0,
    enabled,
    staleTime: 10_000,
  });
}

export function useSaveKeysystemMutation(api: ApiClient) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveKeysystemInput) => api.keysystem.save(data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: KEYSYSTEM_QUERY_ROOT });
    },
  });
}

const SCRIPT_QUERY_ROOT = ['scripts'] as const;

export function useScripts(api: ApiClient, query: ScriptListQuery, enabled: boolean) {
  return useQuery({
    queryKey: [...SCRIPT_QUERY_ROOT, query],
    queryFn: ({ signal }) => api.scripts.list(query, { signal }),
    retry: 0,
    enabled,
    staleTime: 10_000,
  });
}

export function useScriptInvalidation() {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: SCRIPT_QUERY_ROOT });
  }, [client]);
}

export function useCreateScriptMutation(api: ApiClient) {
  const invalidate = useScriptInvalidation();
  return useMutation({
    mutationFn: (data: CreateScriptInput) => api.scripts.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateScriptPlacesMutation(api: ApiClient) {
  const invalidate = useScriptInvalidation();
  return useMutation({
    mutationFn: (input: { scriptId: string; data: UpdateScriptPlacesInput }) =>
      api.scripts.updatePlaces(input.scriptId, input.data),
    onSuccess: invalidate,
  });
}

export function useRemoveScriptMutation(api: ApiClient) {
  const invalidate = useScriptInvalidation();
  return useMutation({
    mutationFn: (scriptId: string) => api.scripts.remove(scriptId),
    onSuccess: invalidate,
  });
}

export function useBuildLoaderMutation(api: ApiClient) {
  return useMutation({
    mutationFn: (data: BuildLoaderInput) => api.scripts.buildLoader(data),
  });
}

export function useObfuscateScriptMutation(api: ApiClient) {
  return useMutation({
    mutationFn: (data: ObfuscateScriptInput) => api.scripts.obfuscate(data),
  });
}
